import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import EmailService, { EmailMessage } from '../feature-module/application/components/email/email.service';
import MailAuthFolderService, { MailAuthFolder } from '../feature-module/application/components/email/mailAuthFolder.service';
import { AuthContext } from './authcontext';
import { useTenantChange } from '../hooks/useTenantChange';
import { showErrorAlert, showSuccessAlert } from '../utils/alertUtils';

interface EmailContextType {
  selectedEmail: EmailMessage | null;
  setSelectedEmail: (email: EmailMessage | null) => void;
  clearSelectedEmail: () => void;
  mailFolders: MailAuthFolder[];
  mailFoldersLoading: boolean;
  mailFoldersOpen: boolean;
  setMailFoldersOpen: (open: boolean) => void;
  renamingMailFolderId: number | null;
  renameMailFolder: (folderId: number, newName: string) => Promise<void>;
  ensureMailFoldersLoaded: () => void;
  getMailFolderById: (folderId: number) => MailAuthFolder | undefined;
  loadMoreMailFolders: () => Promise<void>;
  hasMoreMailFolders: boolean;
  loadingMoreMailFolders: boolean;
}

const EmailContext = createContext<EmailContextType | undefined>(undefined);

export const useEmailContext = () => {
  const context = useContext(EmailContext);
  if (context === undefined) {
    throw new Error('useEmailContext must be used within an EmailProvider');
  }
  return context;
};

interface EmailProviderProps {
  children: ReactNode;
}

export const EmailProvider: React.FC<EmailProviderProps> = ({ children }) => {
  const { auth } = useContext(AuthContext);
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null);
  const [mailFolders, setMailFolders] = useState<MailAuthFolder[]>([]);
  const [mailFoldersLoading, setMailFoldersLoading] = useState(true);
  const [mailFoldersOpen, setMailFoldersOpen] = useState(false);
  const [renamingMailFolderId, setRenamingMailFolderId] = useState<number | null>(null);
  
  const [mailFoldersPage, setMailFoldersPage] = useState(1);
  const [hasMoreMailFolders, setHasMoreMailFolders] = useState(false);
  const [loadingMoreMailFolders, setLoadingMoreMailFolders] = useState(false);

  const mailFoldersLoadedRef = useRef(false);
  const mailFoldersLoadingRef = useRef(false);
  const wasLoadedBeforeTenantChangeRef = useRef(false);

  const clearSelectedEmail = () => {
    setSelectedEmail(null);
  };

  const refreshMailFolders = useCallback(async () => {
    mailFoldersLoadingRef.current = true;
    try {
      setMailFoldersLoading(true);
      const userId = auth?.user_id?.toString();
      let mailAuthIds = '';
      if (userId) {
        const providers = await EmailService.getMailProviders(userId);
        if (providers && providers.length > 0) {
          mailAuthIds = providers.map(p => p.mail_auth_id).filter(id => id).join(',');
        }
      }

      if (!mailAuthIds) {
        setMailFolders([]);
        setMailFoldersPage(1);
        setHasMoreMailFolders(false);
        mailFoldersLoadedRef.current = true;
        return;
      }

      const data = await MailAuthFolderService.listFolders(1, 20, { mail_auth_ids: mailAuthIds });
      setMailFolders(data.results || []);
      setMailFoldersPage(1);
      setHasMoreMailFolders(!!data.next);
      mailFoldersLoadedRef.current = true;
    } catch (err) {
      console.error('Error loading mail auth folders:', err);
    } finally {
      setMailFoldersLoading(false);
      mailFoldersLoadingRef.current = false;
    }
  }, [auth?.user_id]);

  const loadMoreMailFolders = useCallback(async () => {
    if (loadingMoreMailFolders || !hasMoreMailFolders) return;
    
    setLoadingMoreMailFolders(true);
    try {
      const nextPage = mailFoldersPage + 1;
      const userId = auth?.user_id?.toString();
      let mailAuthIds = '';
      if (userId) {
        const providers = await EmailService.getMailProviders(userId);
        if (providers && providers.length > 0) {
          mailAuthIds = providers.map(p => p.mail_auth_id).filter(id => id).join(',');
        }
      }

      if (!mailAuthIds) {
        return;
      }

      const data = await MailAuthFolderService.listFolders(nextPage, 20, { mail_auth_ids: mailAuthIds });
      setMailFolders((prev) => [...prev, ...(data.results || [])]);
      setMailFoldersPage(nextPage);
      setHasMoreMailFolders(!!data.next);
    } catch (err) {
      console.error('Error loading more mail folders:', err);
    } finally {
      setLoadingMoreMailFolders(false);
    }
  }, [mailFoldersPage, hasMoreMailFolders, loadingMoreMailFolders, auth?.user_id]);

  const ensureMailFoldersLoaded = useCallback(() => {
    if (!auth?.user_id) return;
    if (mailFoldersLoadedRef.current || mailFoldersLoadingRef.current) return;
    refreshMailFolders();
  }, [auth?.user_id, refreshMailFolders]);

  useTenantChange({
    clearData: () => {
      wasLoadedBeforeTenantChangeRef.current = mailFoldersLoadedRef.current;
      mailFoldersLoadedRef.current = false;
      setMailFolders([]);
      setMailFoldersPage(1);
      setHasMoreMailFolders(false);
    },
    refreshData: () => {
      if (wasLoadedBeforeTenantChangeRef.current) {
        refreshMailFolders();
      }
    },
  });

  const renameMailFolder = useCallback(async (folderId: number, newName: string) => {
    setRenamingMailFolderId(folderId);
    try {
      const updated = await MailAuthFolderService.renameFolder(folderId, newName);
      setMailFolders((prev) =>
        prev.map((f) => (f.mail_auth_folder_id === folderId ? updated : f))
      );
      showSuccessAlert('Folder renamed successfully');
    } catch (err) {
      console.error('Error renaming folder:', err);
      showErrorAlert('Failed to rename folder');
      throw err;
    } finally {
      setRenamingMailFolderId(null);
    }
  }, []);

  const getMailFolderById = useCallback(
    (folderId: number) => mailFolders.find((f) => f.mail_auth_folder_id === folderId),
    [mailFolders]
  );

  const value: EmailContextType = {
    selectedEmail,
    setSelectedEmail,
    clearSelectedEmail,
    mailFolders,
    mailFoldersLoading,
    mailFoldersOpen,
    setMailFoldersOpen,
    renamingMailFolderId,
    renameMailFolder,
    ensureMailFoldersLoaded,
    getMailFolderById,
    loadMoreMailFolders,
    hasMoreMailFolders,
    loadingMoreMailFolders,
  };

  return (
    <EmailContext.Provider value={value}>
      {children}
    </EmailContext.Provider>
  );
};
